import { useEffect, useMemo, useState } from "react";
import { Button, Card, Empty, Form, Input, Typography, message } from "antd";

import {
	TranslationLanguage,
	TranslationsConfiguration
} from "../../Models";

const { Text } = Typography;
const { TextArea } = Input;

type TranslationsAdminProps = {
	isReadOnly: boolean;
	loading: boolean;
	translations: any;
	translationsConfiguration: TranslationsConfiguration | null;
	onSaveTranslations: (translations: TranslationsConfiguration) => Promise<TranslationsConfiguration>;
	onCreateLanguage: (language: TranslationLanguage) => Promise<TranslationsConfiguration>;
};

const cloneConfiguration = (configuration: TranslationsConfiguration): TranslationsConfiguration => (
	JSON.parse(JSON.stringify(configuration)) as TranslationsConfiguration
);

const stringifyConfiguration = (configuration: TranslationsConfiguration | null) => (
	configuration ? JSON.stringify(configuration, null, 2) : ""
);

const normalizeLanguageCode = (value: string) => value.trim().toLowerCase();

const TranslationsAdmin = ({
	isReadOnly,
	loading,
	translations,
	translationsConfiguration,
	onSaveTranslations,
	onCreateLanguage
}: TranslationsAdminProps) => {
	const t = (key: string, fallback: string) => translations?.[key] || fallback;
	const [draft, setDraft] = useState<TranslationsConfiguration | null>(translationsConfiguration);
	const [jsonValue, setJsonValue] = useState(stringifyConfiguration(translationsConfiguration));
	const [jsonError, setJsonError] = useState("");
	const [saving, setSaving] = useState(false);
	const [creatingLanguage, setCreatingLanguage] = useState(false);
	const [languageForm] = Form.useForm<{ code: string; name: string; nativeName: string }>();
	const [messageApi, contextHolder] = message.useMessage();

	useEffect(() => {
		const nextDraft = translationsConfiguration ? cloneConfiguration(translationsConfiguration) : null;
		setDraft(nextDraft);
		setJsonValue(stringifyConfiguration(nextDraft));
		setJsonError("");
	}, [translationsConfiguration]);

	const stats = useMemo(() => {
		if (!draft) {
			return { languages: 0, keys: 0 };
		}

		const keys = new Set<string>();
		Object.values(draft.translations || {}).forEach(languageTranslations => {
			Object.keys(languageTranslations || {}).forEach(key => keys.add(key));
		});

		return {
			languages: draft.languages.length,
			keys: keys.size
		};
	}, [draft]);

	const parseJsonDraft = () => {
		try {
			const parsed = JSON.parse(jsonValue) as TranslationsConfiguration;
			setJsonError("");
			return parsed;
		} catch (error: any) {
			const nextError = error?.message || t("adminTranslationsJsonInvalid", "El JSON no es válido");
			setJsonError(nextError);
			throw new Error(nextError);
		}
	};

	const handleCreateLanguage = async (values: { code: string; name: string; nativeName: string }) => {
		setCreatingLanguage(true);
		try {
			const savedConfiguration = await onCreateLanguage({
				id: 0,
				code: normalizeLanguageCode(values.code),
				name: values.name.trim(),
				nativeName: (values.nativeName || values.name).trim()
			});

			const nextDraft = cloneConfiguration(savedConfiguration);
			setDraft(nextDraft);
			setJsonValue(stringifyConfiguration(nextDraft));
			setJsonError("");
			languageForm.resetFields();
			messageApi.success(t("adminLanguageCreated", "Idioma creado correctamente"));
		} catch (error: any) {
			messageApi.error(error?.message || t("adminLanguageCreateError", "No se ha podido crear el idioma"));
		} finally {
			setCreatingLanguage(false);
		}
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			const parsedDraft = parseJsonDraft();
			const savedDraft = await onSaveTranslations(parsedDraft);
			const nextDraft = cloneConfiguration(savedDraft);
			setDraft(nextDraft);
			setJsonValue(stringifyConfiguration(nextDraft));
			messageApi.success(t("adminTranslationsSaved", "Traducciones guardadas correctamente"));
		} catch (error: any) {
			messageApi.error(error?.message || t("adminTranslationsSaveError", "No se han podido guardar las traducciones"));
		} finally {
			setSaving(false);
		}
	};

	if (!draft) {
		return (
			<Card loading={loading}>
				{contextHolder}
				<Empty description={t("adminTranslationsEmpty", "No hay traducciones disponibles")} />
			</Card>
		);
	}

	return (
		<div className="translations-admin">
			{contextHolder}
			<Card
				className="templates-static-card"
				title={t("adminTranslationsTitle", "Traducciones")}
				extra={(
					<Button
						type="primary"
						onClick={handleSave}
						loading={saving}
						disabled={isReadOnly}
					>
						{t("save", "Guardar")}
					</Button>
				)}
			>
				<div className="translations-admin-copy">
					<p>
						{t("adminTranslationsDescription", "Edita las traducciones compartidas que se cargan al arrancar la aplicación. Los valores de tipo array usan una línea por opción.")}
					</p>
				</div>

				<Card
					size="small"
					className="translations-admin-card"
					title={t("adminCreateLanguageTitle", "Nuevo idioma")}
				>
					<Form
						layout="vertical"
						form={languageForm}
						onFinish={handleCreateLanguage}
						className="translations-language-form"
					>
						<Form.Item
							label={t("language", "Idioma")}
							name="code"
							rules={[
								{ required: true, message: t("adminLanguageCodeRequired", "El código de idioma es obligatorio") },
								{ pattern: /^[a-z]{2,10}$/i, message: t("adminLanguageCodeInvalid", "Usa solo letras") },
								{
									validator: async (_, value) => {
										const normalizedCode = normalizeLanguageCode(value || "");
										if (!normalizedCode || !draft.languages.some(language => language.code === normalizedCode)) {
											return;
										}

										throw new Error(t("adminLanguageCodeExists", "El código de idioma ya existe"));
									}
								}
							]}
						>
							<Input placeholder={t("adminLanguageCodePlaceholder", "it")} disabled={isReadOnly} />
						</Form.Item>
						<Form.Item
							label={t("name", "Nombre")}
							name="name"
							rules={[{ required: true, message: t("adminLanguageNameRequired", "El nombre del idioma es obligatorio") }]}
						>
							<Input placeholder={t("adminLanguageNamePlaceholder", "Italian")} disabled={isReadOnly} />
						</Form.Item>
						<Form.Item
							label={t("adminNativeLanguageName", "Nombre nativo")}
							name="nativeName"
						>
							<Input placeholder={t("adminNativeLanguageNamePlaceholder", "Italiano")} disabled={isReadOnly} />
						</Form.Item>
						<Form.Item>
							<Button htmlType="submit" disabled={isReadOnly} loading={creatingLanguage}>
								{t("adminCreateLanguageButton", "Crear idioma")}
							</Button>
						</Form.Item>
					</Form>
				</Card>

				<div className="translations-toolbar">
					<Text type="secondary">
						{t("adminTranslationsStats", "Idiomas / claves")}: {stats.languages} / {stats.keys}
					</Text>
				</div>

				<div className="translations-list">
					<div className="translations-row">
						<div className="translations-key">
							<code>translations.json</code>
							<Text type="secondary">
								{t("adminTranslationsJsonHint", "Edita el JSON completo y guarda los cambios.")}
							</Text>
						</div>
						<TextArea
							autoSize={{ minRows: 24, maxRows: 36 }}
							value={jsonValue}
							onChange={(event) => {
								setJsonValue(event.target.value);
								if (jsonError) {
									setJsonError("");
								}
							}}
							disabled={isReadOnly}
							style={{ fontFamily: "Consolas, Monaco, monospace" }}
						/>
						{jsonError && (
							<Text type="danger">{jsonError}</Text>
						)}
					</div>
				</div>
			</Card>
		</div>
	);
};

export default TranslationsAdmin;
